import React, { useContext, useEffect, useState } from 'react';
import { css } from '@emotion/css';
import { useForm } from 'react-hook-form';
import appLabels from '../../utils/appLabels';
import storeLocator from '../../service/store-locator-service';
import { get, has, invoke, isEmpty } from 'lodash';
import { useApp } from '../../context/app-context';
import LoadingComponent from '../../common/LoadingComponent/LoadingComponent';
import SlotPreferences from '../SlotPreferences/SlotPreferences';
import { PreferencesContext, PreferencesContextType } from './PreferencesContext';
import getSlotsInfo from '../../service/get-slots-service';
import { AddressDataType } from '../../service/save-address-service';
import AppLabels from '../../utils/appLabels';
import { ModalContext, ModalContextType } from '../../common/Modal/Modal';
import PropTypes from 'prop-types';
import { SessionContext, SessionContextType } from '../LandingPage/SessioncContext';

const generalStyle = css`
  display: flex;
  flex-direction: column;
  width: 57rem;

  @media (max-width: 641px) {
    width: auto;
  }
`;

const useLocationStyle = css`
  display: flex;
  flex-direction: row;
  color: #7b1a27;
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.125rem;
  align-self: flex-start;
  cursor: pointer;
  background: 0;
  border: 0;
  align-content: center;
  margin-top: 1rem;
`;

const locationSectionStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;

  @media (max-width: 641px) {
    width: 100%;
  }
`;

const upperSectionStyle = css`
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const inputStyle = css`
  display: flex;
  height: 3rem;
  align-items: center;
  flex: 1 0 0;
  border-radius: 3.125rem;
  border: 1px solid #495057;
  background: #fff;
  width: 40rem;
  padding-left: 1.25rem;
  padding-right: 3.5rem;
  -webkit-appearance: none;
  -moz-appearance: textfield;
  margin: 0;

  @media (max-width: 641px) {
    width: 100%;
    box-sizing: border-box;
  }
`;

const locationTextStyle = css`
  margin-left: 0.5rem;
`;

const wrapperStyle = css`
  position: relative;

  @media (max-width: 641px) {
    width: 100%;
  }
`;

const searchIconStyle = css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  transition: initial;
  left: auto;
  right: 1rem;
  cursor: pointer;
`;

const storeItemGeneralStyle = css`
  display: flex;
  justify-content: space-between;
  width: 100%;
  border-bottom: 0.0625rem solid #ddd9d5;
  background: #fff;
  padding: 1rem;
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem;
  box-sizing: border-box;
`;

const notSelectedButtonStyle = css`
  cursor: pointer;
  display: inline-flex;
  padding: 0.5rem 1rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 6.25rem;
  border: 0.0625rem solid #7b1a27;
  color: #7b1a27;
  background: #fff;
`;

const selectedButtonStyle = css`
  display: inline-flex;
  padding: 0.5rem 1rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 6.25rem;
  background: #f1f0ee;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 600;
  line-height: 1.3125rem;
  color: #535250;
  border: none;
`;

const highlightedButtonStyle = css`
  cursor: pointer;
  display: inline-flex;
  padding: 0.5rem 1rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 6.25rem;
  border: 0.0625rem solid #7b1a27;
  background-color: #7b1a27;
  color: #fff;
`;

const itemLogoStyle = css`
  width: 8.625rem;
  height: 1.5rem;
  background: #f1f0ee;
`;

const distanceTextStyle = css`
  text-align: end;
`;

const storeListSectionStyle = css`
  margin-top: 1rem;
  height: 21rem;
  overflow-y: auto;
  overflow-x: hidden;
`;

const footerSectionStyle = css`
  display: flex;
  flex-direction: row-reverse;
  margin-top: 1.5rem;
`;

const submitButtonStyle = css`
  min-width: 16rem;
  border-radius: 6.25rem;
  background: #7b1a27;
  color: #fff;
  border: none;
  font-size: 1rem;
  padding: 1rem 0.75rem;
  line-height: 1.5rem;

  &[disabled] {
    background: #f1f0ee;
    color: #535250;
  }

  @media (max-width: 641px) {
    width: 100%;
  }
`;

const fieldRequiredStyle = css`
  font-size: 0.7rem;
  line-height: 1rem;
  color: #dd1e25;
  flex: 1;
  margin: 0;
`;

const apiEmptyResponseStyle = css`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
`;

const noStoresFoundStyle = css`
  margin-left: 1rem;
  align-self: center;
`;

const carLogo = (
  <svg
    className="svg-icon store-icon svg-icon-DUG-pref-page"
    role="img"
    aria-hidden="true"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
  >
    <g strokeWidth="1" fillRule="evenodd">
      <path d="M21,8.99853401 C21,8.32073 20.4505313,7.77126128 19.7727273,7.77126128 L18.2427273,7.77126128 C17.8467393,6.81904844 17.356094,5.90902486 16.7781818,5.05489765 C15.3013913,4.30246126 13.6561235,3.94185462 12,4.00762492 C10.3438765,3.94185462 8.69860869,4.30246126 7.22181818,5.05489765 C6.64390598,5.90902486 6.15326074,6.81904844 5.75727273,7.77126128 L4.22727273,7.77126128 C3.54946872,7.77126128 3,8.32073 3,8.99853401 C3,9.67633802 3.54946872,10.2258067 4.22727273,10.2258067 L4.70181818,10.2258067 C4.55072659,10.5900637 4.43563298,10.9682284 4.35818182,11.3548976 C4.21909091,12.4430795 4.12090909,15.5112613 4.10454545,16.8448976 C4.08818182,18.178534 4.36636364,19.2012613 5.64272727,19.2012613 C6.05326187,19.2213958 6.45168017,19.0589224 6.7310618,18.7574428 C7.01044344,18.4559632 7.14217659,18.0463522 7.09090909,17.638534 L7.09090909,16.8203522 L16.9090909,16.8203522 L16.9090909,17.638534 C16.860449,18.0450357 16.9933651,18.4523219 17.2724262,18.751879 C17.5514874,19.0514362 17.9483482,19.2128371 18.3572727,19.1930795 C19.6336364,19.1930795 19.9118182,18.1703522 19.8954545,16.8367158 C19.8790909,15.5030795 19.7809091,12.4430795 19.6418182,11.3467158 C19.5638531,10.9628301 19.4487667,10.5874295 19.2981818,10.2258067 L19.7727273,10.2258067 C20.4505313,10.2258067 21,9.67633802 21,8.99853401 Z M8.17090909,6.24126128 C9.39265072,5.76461387 10.6888428,5.50703725 12,5.48035219 C13.3106664,5.50442562 14.6068421,5.75922939 15.8290909,6.23307946 C16.2381818,6.54398855 17.3427273,9.31762492 17.3427273,9.31762492 L6.65727273,9.31762492 C6.65727273,9.31762492 7.76181818,6.54398855 8.17090909,6.24126128 Z M6.84545455,14.0548976 C6.0546832,14.0548976 5.41363636,13.4138508 5.41363636,12.6230795 C5.41363636,11.8323081 6.0546832,11.1912613 6.84545455,11.1912613 C7.63622589,11.1912613 8.27727273,11.8323081 8.27727273,12.6230795 C8.27727273,13.4138508 7.63622589,14.0548976 6.84545455,14.0548976 Z M17.1545455,14.0548976 C16.3637741,14.0548976 15.7227273,13.4138508 15.7227273,12.6230795 C15.7227273,11.8323081 16.3637741,11.1912613 17.1545455,11.1912613 C17.9453168,11.1912613 18.5863636,11.8323081 18.5863636,12.6230795 C18.5863636,13.4138508 17.9453168,14.0548976 17.1545455,14.0548976 Z"></path>
    </g>
  </svg>
);

type Store = {
  bannerLogo: string;
  address1: string;
  address2: string;
  distance: number;
  isSelected: boolean;
  banner: string;
};

const ErrorMessage = ({ errors, name, labels }) => {
  const fieldError = get(errors, `${name}.type`);

  if (has(errors, name)) {
    return (
      <p aria-invalid={true} aria-errormessage={get(labels, fieldError, fieldError)} className={fieldRequiredStyle}>
        {get(labels, fieldError, fieldError)}
      </p>
    );
  } else {
    return null;
  }
};

const PickupTabForm = ({ preferredData, onSave }) => {
  const currentSessionContext = useContext(SessionContext) as SessionContextType;
  const user = get(currentSessionContext, 'user');
  const { appData, showToast } = useApp();
  const [storeApiError, setStoreApiError] = useState(false);
  const [emptyStoreResponse, setEmptyStoreResponse] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [stores, setStores] = useState<Store[]>([]);
  const preferredAddress = get(preferredData, 'address', {});
  const preferredStoreId = get(preferredData, 'storeId', null);
  const preferenceContext = useContext(PreferencesContext) as PreferencesContextType;
  const modalContext = useContext(ModalContext) as ModalContextType;
  const [slotsInfo, setSlotsInfo] = useState({});
  const [selectedStore, setSelectedStore] = useState(null);
  const [savingChanges, setSavingChanges] = useState(false);
  const currentPreferencesContext = useContext(PreferencesContext) as PreferencesContextType;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty, isValid },
    setValue,
    getFieldState,
  } = useForm({
    mode: 'onChange',
  });
  const searchStore = async (data) => {
    try {
      setEmptyStoreResponse(false);
      setStoreApiError(false);
      setIsLoading(true);
      let queryParams = 'size=10&radius=50';
      if (data.zipCode) queryParams += '&zipcode=' + data.zipCode;
      // can be switched to an else when we want exclusive conditions
      if (get(data, 'latitude')) {
        queryParams += '&latitude=' + data.latitude;
        queryParams += '&longitude=' + data.longitude;
      }

      const response = await invoke(currentSessionContext, 'ensureActiveSession', () =>
        storeLocator(appData.environmentVariables, user, queryParams),
      );
      const stores = get(response, 'data.stores.content', [])
        .filter((store) => {
          return (
            store.ecomStore &&
            store.ecomStore.isDUGStore &&
            store.ecomStore.storeFeatures.isAutoReplenishmentEnabled &&
            user.banner === store.polarisBannerName
          );
        })
        .map((store) => {
          return {
            bannerLogo: store.storelogoURL,
            address1: store.address.line1,
            address2: store.address.city + ', ' + store.address.state + ' ' + store.address.zipcode,
            distance: store.distance,
            isSelected:
              store.locationId === preferredStoreId &&
              get(currentPreferencesContext, 'preferencesData.fulfillmentTypes.0.value') === 'DUG',
            locationId: store.locationId,
            banner: store.polarisBannerName,
          };
        });
      if (stores.length === 0) {
        setEmptyStoreResponse(true);
      }

      setStores(stores);
    } catch (error: any) {
      setStores([]);
      if (get(error, 'response.status') === 404) setEmptyStoreResponse(true);
      else setStoreApiError(true);
    }
    setIsLoading(false);
  };

  const analyticsEvents = () => {
    try {
      if (window?.['digitalData']) {
        window['digitalData'].pageFlags.preferences = 'pickup';
        window['digitalData'].modal.modalMessage = 'change-in-pickup-fullfillment';
      }
      if (window?.['eddlLoadComplete']) {
        window?.['AB']['DATALAYER'].setModalClickEvents('ar:fulfillment-edit|pickup-save');
        window['digitalData'].modal.modalMessage = '';
      }
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('@AnalyticsTeam error:', error);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      if (isValid) {
        searchStore({ zipCode: watch('zipCode') });
      }
      event.preventDefault();
    }
  };

  useEffect(() => {
    const isDelivery = get(currentPreferencesContext, 'preferencesData.fulfillmentTypes.0.value') === 'DELIVERY';

    const zipCode = isDelivery
      ? get(currentPreferencesContext, 'preferencesData.addresses.0.zipCode')
      : get(preferredAddress, 'zipCode');

    setValue('zipCode', zipCode);
    searchStore({ zipCode });
  }, []);

  const handleBrowserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          searchStore(position.coords);
        },
        (error) => {
          console.error('Error getting user location:', error);
        },
        { enableHighAccuracy: true, maximumAge: 0 },
      );
    }
  };

  const fetchSlotsInfo = async (): Promise<void> => {
    setSlotsInfo({ loading: true });

    try {
      const storeID: string = get(selectedStore, 'locationId', '');

      if (!isEmpty(storeID)) {
        const resp = await invoke(currentSessionContext, 'ensureActiveSession', () =>
          getSlotsInfo(appData.environmentVariables, user, storeID),
        );
        setSlotsInfo({ data: resp.data, loading: false });
      } else {
        setSlotsInfo({ error: true, loading: false });
      }
    } catch (err) {
      setSlotsInfo({ error: err, loading: false });
    }
  };

  const updateSlotsInfo = async (slotsInfo: any): Promise<any> => {
    const addressData: AddressDataType = {
      guid: get(user, 'customerId', ''),
      fulfillmentType: {
        preferenceDefId: '',
        value: 'DUG',
      },
      slotPreference: {
        slotId: get(slotsInfo, 'slotId', ''),
        fulfillmentDay: get(slotsInfo, 'fulfillmentDay', ''),
        slotStartTS: get(slotsInfo, 'slotStartTS', ''),
        slotEndTS: get(slotsInfo, 'slotEndTS', ''),
        slotType: get(slotsInfo, 'slotType', ''),
        preferenceDefId: '',
      },
      store: {
        storeId: get(selectedStore, 'locationId', ''),
        banner: get(selectedStore, 'banner', ''),
      },
    };

    try {
      await invoke(preferenceContext, 'saveAddress', addressData);
      await invoke(preferenceContext, 'fetchPreferences');

      showToast('info', 'You have successfully updated your preferences.');
      onSave();
    } catch (err) {
      invoke(modalContext, 'setModalError', AppLabels.DeliveryTabForm.LOADING_ERROR);
    }
  };

  const selectStore = (store) => {
    if (get(store, 'locationId')) {
      setSelectedStore(store);
    }
  };

  stores.sort((a, b) => a.distance - b.distance);

  const storeList = stores.map((store, index) => {
    const isHighlighted = get(selectedStore, 'locationId') === get(store, 'locationId');

    return (
      <div className={storeItemGeneralStyle} key={index}>
        <div aria-label={`${store.banner}, ${store.address1}, ${store.address2}`}>
          <img className={itemLogoStyle} src={store.bannerLogo} alt={'logo'} aria-hidden={true} />
          <div aria-hidden={true}>{store.address1}</div>
          <div aria-hidden={true}>{store.address2}</div>
        </div>

        <div>
          <div className={distanceTextStyle}>{store.distance}mi.</div>
          <button
            tabIndex={0}
            aria-label={store.isSelected ? 'Selected store' : `Select store ${store.address1}, ${store.address2}`}
            aria-roledescription="button"
            disabled={store.isSelected}
            onClick={(evt) => {
              evt.preventDefault();

              if (get(selectedStore, 'locationId') === get(store, 'locationId')) {
                setSelectedStore(null);
              } else {
                selectStore(store);
              }
            }}
            className={
              store.isSelected ? selectedButtonStyle : isHighlighted ? highlightedButtonStyle : notSelectedButtonStyle
            }
          >
            {store.isSelected || isHighlighted ? 'Selected' : 'Select'}
          </button>
        </div>
      </div>
    );
  });

  const tabLabel = savingChanges ? 'Select preferred slot' : 'Select store for pickup';

  return (
    <div className={generalStyle} aria-live="polite" aria-atomic={false} aria-label={tabLabel}>
      {savingChanges ? (
        <PreferencesContext.Provider
          value={{
            fetchSlotsInfo,
            slotsInfo,
            updateSlotsInfo,
          }}
        >
          <SlotPreferences pickup={true} isSelectionCheckEnabled={false} />
        </PreferencesContext.Provider>
      ) : (
        <form className={`item-list-container ${generalStyle}`} onSubmit={handleSubmit(searchStore)}>
          <div className={upperSectionStyle}>
            <div className={locationSectionStyle}>
              <button
                tabIndex={0}
                className={useLocationStyle}
                aria-label="Use my current location"
                aria-roledescription="button"
                onClick={handleBrowserLocation}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <g clipPath="url(#clip0_198_50798)">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7.99935 3.1665C5.32997 3.1665 3.16602 5.33046 3.16602 7.99984C3.16602 10.6692 5.32997 12.8332 7.99935 12.8332C10.6687 12.8332 12.8327 10.6692 12.8327 7.99984C12.8327 5.33046 10.6687 3.1665 7.99935 3.1665ZM2.16602 7.99984C2.16602 4.77818 4.77769 2.1665 7.99935 2.1665C11.221 2.1665 13.8327 4.77818 13.8327 7.99984C13.8327 11.2215 11.221 13.8332 7.99935 13.8332C4.77769 13.8332 2.16602 11.2215 2.16602 7.99984Z"
                      fill="#7B1A27"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7.99935 0.666504C8.27549 0.666504 8.49935 0.890362 8.49935 1.1665V2.4665C8.49935 2.74265 8.27549 2.9665 7.99935 2.9665C7.72321 2.9665 7.49935 2.74265 7.49935 2.4665V1.1665C7.49935 0.890362 7.72321 0.666504 7.99935 0.666504Z"
                      fill="#7B1A27"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7.99935 12.8332C8.27549 12.8332 8.49935 13.057 8.49935 13.3332V14.6665C8.49935 14.9426 8.27549 15.1665 7.99935 15.1665C7.72321 15.1665 7.49935 14.9426 7.49935 14.6665V13.3332C7.49935 13.057 7.72321 12.8332 7.99935 12.8332Z"
                      fill="#7B1A27"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.666016 7.99984C0.666016 7.72369 0.889873 7.49984 1.16602 7.49984H2.46602C2.74216 7.49984 2.96602 7.72369 2.96602 7.99984C2.96602 8.27598 2.74216 8.49984 2.46602 8.49984H1.16602C0.889873 8.49984 0.666016 8.27598 0.666016 7.99984Z"
                      fill="#7B1A27"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12.966 7.99984C12.966 7.72369 13.1899 7.49984 13.466 7.49984H14.8327C15.1088 7.49984 15.3327 7.72369 15.3327 7.99984C15.3327 8.27598 15.1088 8.49984 14.8327 8.49984H13.466C13.1899 8.49984 12.966 8.27598 12.966 7.99984Z"
                      fill="#7B1A27"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7.99935 6.49984C7.17092 6.49984 6.49935 7.17141 6.49935 7.99984C6.49935 8.82826 7.17092 9.49984 7.99935 9.49984C8.82778 9.49984 9.49935 8.82826 9.49935 7.99984C9.49935 7.17141 8.82778 6.49984 7.99935 6.49984ZM5.49935 7.99984C5.49935 6.61913 6.61864 5.49984 7.99935 5.49984C9.38006 5.49984 10.4993 6.61913 10.4993 7.99984C10.4993 9.38055 9.38006 10.4998 7.99935 10.4998C6.61864 10.4998 5.49935 9.38055 5.49935 7.99984Z"
                      fill="#7B1A27"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_198_50798">
                      <rect width="16" height="16" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <p className={locationTextStyle}>{appLabels.PickupTabForm.useMyLocation}</p>
              </button>
              <div className={wrapperStyle}>
                <input
                  data-testid="zipcode-search"
                  onKeyDown={handleKeyDown}
                  className={inputStyle}
                  type="text"
                  maxLength={5}
                  aria-invalid={get(getFieldState('zipCode'), 'invalid')}
                  {...register('zipCode', {
                    pattern: {
                      value: /^[0-9]+$/,
                      message: 'Please enter a valid zip code',
                    },
                    required: true,
                    validate: (value) => {
                      //using this instead of minLength so regexp takes precedence
                      return get(value, 'length') >= 5;
                    },
                  })}
                />
                <div className={searchIconStyle} onClick={handleSubmit(searchStore)} data-testid="search-button">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M10.75 4C7.02208 4 4 7.02208 4 10.75C4 14.4779 7.02208 17.5 10.75 17.5C14.4779 17.5 17.5 14.4779 17.5 10.75C17.5 7.02208 14.4779 4 10.75 4ZM3 10.75C3 6.46979 6.46979 3 10.75 3C15.0302 3 18.5 6.46979 18.5 10.75C18.5 15.0302 15.0302 18.5 10.75 18.5C6.46979 18.5 3 15.0302 3 10.75Z"
                      fill="#7B1A27"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M15.6464 15.6464C15.8417 15.4512 16.1583 15.4512 16.3536 15.6464L16 16L15.6464 16.3536C15.4512 16.1583 15.4512 15.8417 15.6464 15.6464ZM16 16L15.6464 16.3536L20.1465 20.8536C20.3417 21.0488 20.6583 21.0488 20.8536 20.8535C21.0488 20.6583 21.0488 20.3417 20.8535 20.1464L16.3543 15.6472L16 16Z"
                      fill="#7B1A27"
                    />
                  </svg>
                </div>
              </div>
              <ErrorMessage
                errors={errors}
                name="zipCode"
                labels={{
                  required: appLabels.PickupTabForm.zipCodeRequired,
                  pattern: appLabels.PickupTabForm.zipCodePattern,
                  validate: appLabels.PickupTabForm.zipCodeMinLength,
                }}
              ></ErrorMessage>
            </div>
          </div>
          <div className={`item-list-container ${storeListSectionStyle}`}>
            {isLoading && <LoadingComponent />}
            {!isLoading &&
              (!emptyStoreResponse ? (
                storeList
              ) : (
                <div className={apiEmptyResponseStyle}>
                  <div>{carLogo}</div>
                  <div className={noStoresFoundStyle}>{appLabels.PickupTabForm.noStoresFound}</div>
                </div>
              ))}
          </div>
          <div className={footerSectionStyle}>
            <input
              data-testid="save-button"
              className={submitButtonStyle}
              type="submit"
              value="Save and continue"
              disabled={!get(selectedStore, 'locationId')}
              onClick={(evt) => {
                evt.preventDefault();
                setSavingChanges(true);
                analyticsEvents();
              }}
            />
          </div>
        </form>
      )}
    </div>
  );
};

PickupTabForm.propTypes = {
  onSave: PropTypes.func,
};

export default PickupTabForm;
